<template>
  <TransitionRoot
    :show="modelValue"
    as="template"
    enter="duration-300 ease-out"
    enter-from="opacity-0"
    enter-to="opacity-100"
  >
    <Dialog
      as="div"
      class="relative z-40"
      @close="emit('update:modelValue', false)"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
      >
        <div
          class="fixed inset-0 bg-black/40"
          aria-hidden="true"
        />
      </TransitionChild>
      <div class="fixed inset-0 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="relative flex w-full max-w-md flex-col rounded-xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-w-lg lg:max-w-2xl xl:max-w-3xl"
          >
            <div class="flex shrink-0 items-center justify-between border-b border-gray-100 p-4 sm:px-6 lg:px-8">
              <h3 class="text-lg font-semibold text-gray-900 sm:text-xl lg:text-2xl">Propuesta de modificación</h3>
              <button
                class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
                @click="emit('update:modelValue', false)"
              >
                <XMarkIcon class="size-5 sm:size-6" />
              </button>
            </div>
            <div class="mt-3 shrink-0 border-b border-gray-50 px-4 sm:px-6 lg:px-8">
              <sub-order-modifications-progress-bar :current-step="currentStep" />
            </div>
            <div class="flex min-h-0 flex-1 flex-col bg-gray-50/50 text-gray-700">
              <sub-order-modifications-product-adjustments
                v-if="currentStep === 'adjust'"
                class="flex flex-1 flex-col p-4 sm:p-6 lg:p-8"
                :sub-order-products="subOrderProducts"
                :product-modifications="productModificationRecords"
                @modifications-updated="updateProductModifications"
                @next-step="nextStep"
              />
              <sub-order-modifications-recommendations
                v-if="currentStep === 'recommend'"
                class="flex flex-1 flex-col p-4 sm:p-6 lg:p-8"
                :products="products"
                :products-query-is-loading="productsQueryIsLoading"
                :selected-products="selectedProducts"
                :selected-product-quantities="selectedProductQuantities"
                @selections-updated="updateSelections"
                @previous-step="previousStep"
                @next-step="nextStep"
              />
              <sub-order-modifications-summary
                v-if="currentStep === 'confirm'"
                class="flex flex-1 flex-col p-4 sm:p-6 lg:p-8"
                :product-modifications-records="productModificationRecords"
                :selected-products="selectedProducts"
                :selected-product-quantities="selectedProductQuantities"
                :is-loading="isLoading"
                :has-changes="hasChanges"
                @previous-step="previousStep"
                @confirm-changes="confirmChanges"
              />
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { useQuery, useMutation } from 'vue-query';
import productsApi from 'api/products';
import subOrderProductModificationsApi from 'api/sub-order-product-modifications';
import useMessageStore from 'stores/message-store';
import SubOrderModificationsProgressBar from './sub-order-modifications-progress-bar.vue';
import SubOrderModificationsProductAdjustments from './sub-order-modifications-product-adjustments.vue';
import SubOrderModificationsRecommendations from './sub-order-modifications-recommendations.vue';
import SubOrderModificationsSummary from './sub-order-modifications-summary.vue';

type StepName = 'adjust' | 'recommend' | 'confirm';

const MAX_PRODUCTS = 150;
const STEPS = ['adjust', 'recommend', 'confirm'] as const;

interface SubOrderModificationsDialogProps {
  modelValue: boolean;
  maker: Maker;
  subOrder: SubOrder;
  subOrderProducts: SubOrderProduct[];
}

const props = withDefaults(defineProps<SubOrderModificationsDialogProps>(), { modelValue: false });
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; 'update:subOrder': [] }>();

const messageStore = useMessageStore();
const currentStep = ref<StepName>('adjust');
const productModificationRecords = ref<Record<number, ProductModification>>({});
const selectedProducts = ref<Product[]>([]);
const selectedProductQuantities = ref<Record<string, number>>({});

const productsQueryParams = computed(() => {
  let queryParams = `q[maker_id_eq]=${props.maker.id}&q[s]=maker_priority asc`;
  props.subOrderProducts.forEach((subOrderProduct) => {
    queryParams += `&q[id_not_in][]=${subOrderProduct.productId}`;
  });
  return queryParams;
});

const productsQuery = useQuery(
  ['products', props.maker.id],
  () => productsApi.active(1, productsQueryParams.value, MAX_PRODUCTS),
  { enabled: computed(() => !!props.maker?.id), keepPreviousData: true },
);

const productsQueryIsLoading = computed(() => productsQuery.isLoading.value);
const products = computed(() => productsQuery.data.value?.data.products as Product[] | undefined);
const hasChanges = computed(
  () => Object.keys(productModificationRecords.value).length > 0 || selectedProducts.value.length > 0,
);

function updateProductModifications(newProductModifications: Record<number, ProductModification>) {
  productModificationRecords.value = newProductModifications;
}

function updateSelections(products: Product[], quantities: Record<string, number>) {
  selectedProducts.value = products;
  selectedProductQuantities.value = quantities;
}

function nextStep() {
  const currentIndex = STEPS.indexOf(currentStep.value);
  if (currentIndex < STEPS.length - 1) {
    currentStep.value = STEPS[currentIndex + 1];
  }
}

function previousStep() {
  const currentIndex = STEPS.indexOf(currentStep.value);
  if (currentIndex > 0) {
    currentStep.value = STEPS[currentIndex - 1];
  }
}

const createSubOrderProductModifications = useMutation(
  () => {
    const data: Partial<SubOrderProductModification>[] = [];
    Object.values(productModificationRecords.value).forEach((productModification) => {
      data.push({
        productId: productModification.subOrderProduct.productId,
        subOrderId: props.subOrder.id,
        originalQuantity: productModification.originalQuantity,
        newQuantity: productModification.newQuantity,
        replacementTime: productModification.replacementTime || '',
      });
    });

    selectedProducts.value.forEach((product) => {
      const quantity = selectedProductQuantities.value[product.id];
      if (quantity && quantity > 0) {
        data.push({
          productId: product.id,
          subOrderId: props.subOrder.id,
          originalQuantity: 0,
          newQuantity: quantity,
          replacementTime: '',
        });
      }
    });

    return subOrderProductModificationsApi.bulkCreate(data);
  },
  {
    onSuccess: () => {
      messageStore.showMessage('Propuesta creada correctamente.', 'success');
      emit('update:subOrder');
      emit('update:modelValue', false);
      resetState();
    },
  },
);

const isLoading = computed(() => createSubOrderProductModifications.isLoading.value);

function confirmChanges() {
  if (hasChanges.value) {
    createSubOrderProductModifications.mutate();
  }
}

function resetState() {
  currentStep.value = 'adjust';
  productModificationRecords.value = {};
  selectedProducts.value = [];
  selectedProductQuantities.value = {};
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      resetState();
    }
  },
);
</script>
